import React, { useCallback } from 'react';
import ErrorBoundary from '../components/ErrorBoundary';
import ComponentWrapper from '../components/ComponentWrapper';
import MinimalTimeComponent from '../components/MinimalTimeComponent';
import GridItem from '../components/GridItem';
import { getDynamicPropsOverrides, isTimeComponent, componentUsesMarkers } from './dynamicProps';
import { GRID_CONFIG } from '../constants/gridConfig';

/**
 * Create component renderer hook
 * @param {Object} gridConfig - Grid configuration
 * @returns {Object} Component rendering functions
 */
export const useComponentRenderer = (gridConfig) => {
  // Stable component renderer - time handling now isolated
  const renderComponent = useCallback((transformedComponent, imageUrl, markersData = [], planData = null, legacySignage = null, categoriesData = null, shortName = null) => {
    const { componentName, displayName, Component, props } = transformedComponent;
    
    if (!Component) {
      return <ErrorBoundary componentName={componentName} />;
    }

    try {
      // Check if this is a time-dependent component
      const isTimeComp = isTimeComponent(displayName);
      
      if (isTimeComp) {
        // Use minimal time component - only updates when data changes
        const baseProps = {
          ...props,
          ...getDynamicPropsOverrides(componentName, displayName, props, imageUrl, markersData, planData, legacySignage, categoriesData, shortName)
        };
        
        return (
          <MinimalTimeComponent 
            Component={Component}
            componentProps={baseProps}
            componentName={componentName}
            displayName={displayName}
          />
        );
      }
      
      // For non-time components, render normally
      const componentProps = {
        ...props,
        ...getDynamicPropsOverrides(componentName, displayName, props, imageUrl, markersData, planData, legacySignage, categoriesData, shortName)
      };

      return (
        <ComponentWrapper>
          <Component 
            {...componentProps} 
            key={componentName}
          />
        </ComponentWrapper>
      );
    } catch (error) {
      return <ErrorBoundary error={error} componentName={componentName} />;
    }
  }, []);

  // Stable component renderer function - no time dependencies
  const renderGridComponent = useCallback((transformedComponent, imageUrl, markersData, planData, isMainArea = true, legacySignage = null, categoriesData = null, shortName = null) => {
    const { layout } = transformedComponent;
    
    // Calculate position using grid layout system
    const colWidth = isMainArea ? gridConfig.columnWidth : gridConfig.sideColumnWidth;
    
    let style = {
      position: 'absolute',
      left: 0,
      top: 0,
      width: GRID_CONFIG.DEFAULT_COMPONENT_WIDTH * 50,
      height: GRID_CONFIG.DEFAULT_COMPONENT_HEIGHT * 50
    };

    if (layout && colWidth > 0) {
      const left = layout.x * (colWidth + gridConfig.marginX);
      const top = layout.y * (gridConfig.rowHeight + gridConfig.marginY);
      const width = Math.max(layout.w * colWidth + (layout.w - 1) * gridConfig.marginX, 100);
      const height = Math.max(layout.h * gridConfig.rowHeight + (layout.h - 1) * gridConfig.marginY, 60);
      
      style = {
        position: 'absolute',
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${height}px`
      };
    }

    // Only pass markers to Floor Plan components to prevent unnecessary re-renders
    const optimizedMarkersData = componentUsesMarkers(transformedComponent.displayName) ? markersData : [];

    return (
      <GridItem
        key={String(transformedComponent.id)}
        transformedComponent={transformedComponent}
        imageUrl={imageUrl}
        markersData={optimizedMarkersData}
        planData={planData}
        isMainArea={isMainArea}
        legacySignage={legacySignage}
        categoriesData={categoriesData}
        shortName={shortName}
        style={style}
        renderComponent={renderComponent}
      />
    );
  }, [gridConfig, renderComponent]);

  return {
    renderComponent,
    renderGridComponent
  };
};
